import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo} from 'react';
import {Node, XYPosition} from 'reactflow';

import {
  ProcessFlowchartNodeSection,
  ProcessFlowchartNodeType,
} from '../processFlowchartConfigEditorConstants';
import {NODE_TYPE_TO_NAME, NODE_TYPE_TO_SECTION} from '../processFlowchartConfigEditorRegistry';
import {NODE_TYPE_TO_RENDERER} from '../processFlowchartConfigEditorRegistry';
import {FlowchartOptions} from '../processFlowchartConfigEditorUtils';
import {getLogicNodeCreationContext, getCreateLogicNodeName, getNewLogicNode} from './logicNodeCreationUtils';
import {NodeSidepanelSectionProps} from './NodeSidepanelSection';
import {DEFAULT_NODE_PROPS, DraggableNode} from './sidepanelNodeUtils';

function DraggableLogicNode(props: {
  nodeType: ProcessFlowchartNodeType;
  addNodeToCanvas: (node: Node<unknown>) => void;
  options: FlowchartOptions;
  reactFlowWrapper: React.RefObject<HTMLDivElement>;
}): JSX.Element {
  const getDefaultName = useCallback(
    (nodeType: ProcessFlowchartNodeType): string => getCreateLogicNodeName(nodeType),
    []
  );

  const staticNodeData = useMemo(() => getLogicNodeCreationContext(props.nodeType), [props.nodeType]);

  const addNode = useCallback(
    (position: XYPosition) => {
      const newNode = getNewLogicNode(staticNodeData, position, props.options);
      props.addNodeToCanvas(newNode);
    },
    [props, staticNodeData]
  );

  const nodeRenderer = useCallback(
    ({dragging}: {dragging: boolean}) =>
      NODE_TYPE_TO_RENDERER[props.nodeType]({
        ...DEFAULT_NODE_PROPS,
        id: `${props.nodeType}-sidepanel-node`,
        type: props.nodeType,
        data: {
          ...staticNodeData,
          nodeName: getDefaultName(props.nodeType),
          sections: [],
          hideSectionSummaries: true,
          rowRenderer: () => null,
          nodeWidth: '100%',
        },
        dragging,
      }),
    [props.nodeType, staticNodeData, getDefaultName]
  );

  return (
    <DraggableNode
      addNode={addNode}
      nodeRenderer={nodeRenderer}
      reactFlowWrapper={props.reactFlowWrapper}
      styleOverrides={[styles.noMargin]}
      dataTestAttrs={{
        component: 'LogicNodeSidepanelSection',
        element: 'DraggableLogicNode',
        key: props.nodeType,
      }}
    />
  );
}

/**
 * A sidepanel component that displays all available task nodes that can be added to the flowchart
 */
export default function LogicNodeSidepanelSection(
  props: Omit<NodeSidepanelSectionProps, 'section'>
): JSX.Element {
  const logicNodeTypes = props.nodeTypes.filter(
    (nodeType) => NODE_TYPE_TO_SECTION[nodeType] === ProcessFlowchartNodeSection.LOGIC
  );
  return (
    <div className={css(styles.logicNodesContainer)}>
      {logicNodeTypes
        .filter((nodeType) => {
          if (props.searchQuery == null) {
            return true;
          }
          return NODE_TYPE_TO_NAME[nodeType].toLowerCase().includes(props.searchQuery.toLowerCase());
        })
        .map((nodeType) => (
          <DraggableLogicNode
            key={nodeType}
            nodeType={nodeType}
            addNodeToCanvas={props.addNodeToCanvas}
            options={props.options}
            reactFlowWrapper={props.reactFlowWrapper}
          />
        ))}
    </div>
  );
}

const styles = StyleSheet.create({
  logicNodesContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    width: '100%',
    gap: 12,
  },
  noMargin: {
    margin: 0,
  },
});
